import {
  Permission,
  type SimulationListEntryDto,
  SimulationStatus,
  type UserDto,
} from '@/api';
import PermissionWrapper from '@/components/auth/permission-wrapper';
import { VerticalSeparator } from '@/components/table/toolbar/vertical-separator';
import { useAuthContext } from '@/context/auth-context';
import { useUserRole } from '@/hooks/use-user-role';
import { ROUTES } from '@/routes';
import { themeColors } from '@/theme/colors';
import { formatDateDisplay, formatDateTimeDisplay } from '@/utils/date-utils';
import { Button, TextLink } from '@lego/connect-components-react';
import { Flex, Text } from '@lego/klik-ui';
import {
  Tooltip as MuiTooltip,
  ThemeProvider,
  createTheme,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import {
  type DropdownOption,
  type MRT_Cell,
  type MRT_ColumnDef,
  MRT_GlobalFilterTextField,
  type MRT_RowSelectionState,
  MRT_ShowHideColumnsButton,
  MRT_ToggleFiltersButton,
  MRT_ToggleFullScreenButton,
  MaterialReactTable,
} from 'material-react-table';
import { type RefObject, useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FilteredEmptyList } from '../filtered-empty-list';
import { StatusTag } from '../status-tag';
import SimulationListBulkActionsBar from './simulation-list-bulk-actions-bar';

const USER_DTO_KEYS = new Set<keyof SimulationListEntryDto>([
  'createdBy',
  'assignedTo',
]);

type SimulationListProps = {
  onNewSimulationClick: () => void;
  data: SimulationListEntryDto[];
  newSimulationTriggerRef: RefObject<HTMLButtonElement | null>;
  isLoading?: boolean;
};

const SimulationList = ({
  onNewSimulationClick,
  data,
  newSimulationTriggerRef,
  isLoading,
}: SimulationListProps) => {
  const navigate = useNavigate();
  const theme = createTheme({
    palette: {
      mode: 'light',
    },
  });

  const memoizedData = useMemo(() => Array.from(data.values()), [data]);
  const isNonNavigableStatus = useCallback((status: SimulationStatus) => {
    return status === SimulationStatus.Error;
  }, []);

  const columnData: Record<
    keyof Omit<SimulationListEntryDto, 'simulationId' | 'watcherCount'>,
    { label: string; size: number; filterVariant: string }
  > = {
    simulationName: {
      label: 'Name',
      size: 300,
      filterVariant: 'autocomplete',
    },
    status: {
      label: 'Status',
      size: 180,
      filterVariant: 'multi-select',
    },
    createdBy: {
      label: 'Created by',
      size: 180,
      filterVariant: 'multi-select',
    },
    assignedTo: {
      label: 'Assigned to',
      size: 180,
      filterVariant: 'multi-select',
    },
    watchers: {
      label: 'Watchers',
      size: 160,
      filterVariant: 'multi-select',
    },

    createdAt: {
      label: 'Created at',
      size: 180,
      filterVariant: 'date-range',
    },
    planningDate: {
      label: 'Planning Date',
      size: 180,
      filterVariant: 'date-range',
    },
    year: {
      label: 'Planning Year',
      size: 180,
      filterVariant: 'multi-select',
    },
    marketGroup: {
      label: 'Mkt group',
      size: 160,
      filterVariant: 'multi-select',
    },
    subMarketGroups: {
      label: 'BU',
      size: 110,
      filterVariant: 'multi-select',
    },
    priceUnitIds: {
      label: 'Price lists',
      size: 270,
      filterVariant: 'multi-select',
    },
  };
  const { isGlobalPriceManager, isLocalPriceManager } = useUserRole();
  const { user } = useAuthContext();

  const defaultColumnFilters = useMemo(
    () =>
      isGlobalPriceManager && user?.name
        ? [{ id: 'createdBy', value: [user.name] }]
        : [],
    [isGlobalPriceManager, user?.name],
  );

  // Hide role-gated columns from the table.
  const hiddenColumns: Set<string> = new Set();
  if (!isGlobalPriceManager) hiddenColumns.add('watchers');

  const columnKeys = (
    Object.keys(columnData) as (keyof typeof columnData)[]
  ).filter((k) => !hiddenColumns.has(k));

  const getCellComponent = useCallback(
    (
      columnKey: keyof SimulationListEntryDto,
      cell: MRT_Cell<SimulationListEntryDto, unknown>,
    ) => {
      switch (columnKey) {
        case 'simulationName':
          return (
            <>
              {isNonNavigableStatus(cell.row.original.status) ? (
                <Text className="body-sm-regular">
                  {cell.row.original.simulationName}
                </Text>
              ) : (
                <TextLink
                  size={'small'}
                  href={`${ROUTES.SimulationTool.staticPath}/${cell.row.original.simulationId}`}
                  tabIndex={0}
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(
                      `${ROUTES.SimulationTool.staticPath}/${cell.row.original.simulationId}`,
                    );
                  }}
                  variant="emphasis"
                  style={{ cursor: 'pointer' }}
                >
                  {cell.row.original.simulationName}
                </TextLink>
              )}
            </>
          );
        case 'status':
          return <StatusTag status={cell.row.original.status} size="small" />;
        case 'createdBy':
          return (
            <Text className="body-xs-regular">
              {cell.row.original.createdBy.name}
            </Text>
          );
        case 'assignedTo':
          return (
            <Text className="body-xs-regular">
              {cell.row.original.assignedTo.name}
            </Text>
          );
        case 'createdAt':
          return (
            <Text className="body-xs-regular">
              {cell.row.original.createdAt
                ? formatDateTimeDisplay(new Date(cell.row.original.createdAt))
                : '-'}
            </Text>
          );
        case 'planningDate':
          return (
            <Text className="body-xs-regular">
              {cell.row.original.planningDate
                ? formatDateDisplay(new Date(cell.row.original.planningDate))
                : '-'}
            </Text>
          );
        case 'watchers': {
          const watcherList = cell.row.original.watchers ?? [];
          if (watcherList.length === 0) {
            return (
              <Text
                className="body-xs-regular"
                color={themeColors.content.subtle}
              >
                —
              </Text>
            );
          }
          const visibleNames = watcherList
            .slice(0, 2)
            .map((w) => w.fullName)
            .join(', ');
          const allNames = watcherList.map((w) => w.fullName).join(', ');
          const overflow =
            watcherList.length > 2 ? ` +${watcherList.length - 2}` : '';
          return (
            <MuiTooltip title={allNames} arrow placement="top">
              <Text className="body-xs-regular">
                {visibleNames}
                {overflow}
              </Text>
            </MuiTooltip>
          );
        }
        default:
          return (
            <Text className="body-xs-regular">
              {Array.isArray(cell.row.original[columnKey])
                ? Array.from(new Set(cell.row.original[columnKey])).join(', ')
                : cell.row.original[columnKey]}
            </Text>
          );
      }
    },
    [isNonNavigableStatus, navigate],
  );

  const getFilterOptions = useCallback(
    (key: keyof SimulationListEntryDto) => {
      const getValue = USER_DTO_KEYS.has(key)
        ? (pt: SimulationListEntryDto) => [(pt[key] as UserDto).name]
        : key === 'watchers'
          ? (pt: SimulationListEntryDto) =>
              (pt.watchers ?? []).map((w) => w.fullName)
          : (pt: SimulationListEntryDto) =>
              Array.isArray(pt[key]) ? pt[key] : [pt[key]];
      const normalizedValues = data
        .flatMap(getValue)
        .map((value) => String(value ?? ''));
      if (key === 'createdBy' && user?.name) {
        normalizedValues.push(user.name);
      }
      return Array.from(new Set(normalizedValues))
        .sort((a, b) => a.localeCompare(b))
        .map(
          (value) =>
            ({
              label: value === '' ? '(empty)' : value,
              value,
            }) as DropdownOption,
        );
    },
    [data, user?.name],
  );

  const columns = useMemo<MRT_ColumnDef<SimulationListEntryDto>[]>(() => {
    return columnKeys.map((key) => {
      const col = columnData[key];
      const isUserDtoField = USER_DTO_KEYS.has(key);
      const isWatcherColumn = key === 'watchers';
      const columnDef: MRT_ColumnDef<SimulationListEntryDto> = {
        ...(isUserDtoField
          ? { accessorFn: (row) => (row[key] as UserDto).name, id: key }
          : isWatcherColumn
            ? {
                accessorFn: (row) =>
                  (row.watchers ?? []).map((w) => w.fullName),
                id: key,
              }
            : { accessorKey: key }),
        header: col.label ?? key,
        size: col.size,
        filterVariant:
          col.filterVariant as MRT_ColumnDef<SimulationListEntryDto>['filterVariant'],
        filterSelectOptions: getFilterOptions(key),
        Header: () => (
          <MuiTooltip title={col.label} placement="top">
            <Flex alignItems="center" gap="4px" minHeight="28px">
              <Text className="body-xs-medium">{col.label}</Text>
            </Flex>
          </MuiTooltip>
        ),
        Cell: ({ cell }) =>
          getCellComponent(key as keyof SimulationListEntryDto, cell),
        enableSorting: key !== 'subMarketGroups' && key !== 'priceUnitIds',
      };

      // Add custom filter function for year (multi-select wasn't working with numbers) and marketGroup (to be able to filter by empty value)
      if (key === 'year' || key === 'marketGroup') {
        columnDef.filterFn = (row, id, filterValues) =>
          !filterValues?.length ||
          filterValues.includes(String(row.getValue(id) ?? ''));
      }

      // Watchers filter: match if any selected watcher name is in the row's watcher list
      if (key === 'watchers') {
        columnDef.filterFn = (row, _id, filterValues) => {
          if (!filterValues?.length) return true;
          const names = (row.original.watchers ?? []).map((w) => w.fullName);
          return filterValues.some((fv: string) => names.includes(fv));
        };
      }

      return columnDef;
    });
  }, [columnKeys, getCellComponent, getFilterOptions]);

  const [rowSelection, setRowSelection] = useState<{
    state: MRT_RowSelectionState;
    items: SimulationListEntryDto[];
  }>({ state: {}, items: [] });

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        width: '100%',
      }}
    >
      <div
        style={{
          display: 'flex',
          flex: '1 1 auto',
          flexDirection: 'column',
          height: '100%',
          minHeight: 0,
          width: '100%',
        }}
      >
        <ThemeProvider theme={theme}>
          <LocalizationProvider
            dateAdapter={AdapterDayjs}
            dateFormats={{ keyboardDate: 'DD/MM/YYYY' }}
          >
            <MaterialReactTable
              columns={columns}
              data={memoizedData}
              muiTablePaperProps={{
                sx: {
                  border: 0,
                  borderRadius: 0,
                  boxShadow: 'none',
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                  minHeight: 0,
                  width: '100%',
                },
              }}
              getRowId={(row) => row.simulationId.toString()}
              enableRowSelection={!isLocalPriceManager}
              enableStickyHeader
              enableColumnResizing
              enableColumnActions={false}
              enableColumnFilterModes={false}
              enablePagination={false}
              state={{ isLoading, rowSelection: rowSelection.state }}
              initialState={{
                columnVisibility: { createdAt: true, owner: false },
                columnFilters: defaultColumnFilters,
                sorting: [{ id: 'createdAt', desc: true }],
                showColumnFilters: true,
                showGlobalFilter: true,
                density: 'compact',
              }}
              onRowSelectionChange={(getNewState) => {
                const selectedRowIds =
                  typeof getNewState === 'function'
                    ? getNewState(rowSelection.state)
                    : getNewState;

                const selectedItems = Array.from(data.values()).filter(
                  (item) => selectedRowIds[item.simulationId],
                );
                setRowSelection({
                  state: selectedRowIds,
                  items: selectedItems,
                });
              }}
              displayColumnDefOptions={{
                'mrt-row-select': {
                  //resize checkbox column
                  size: 50,
                },
              }}
              renderTopToolbar={({ table }) => {
                return (
                  <Flex margin="8px 16px" justifyContent="space-between">
                    <Flex gap="8px" alignItems="center">
                      <MRT_GlobalFilterTextField
                        table={table}
                        placeholder="Search"
                        fullWidth
                      />
                      <MRT_ToggleFiltersButton table={table} />
                      <MRT_ShowHideColumnsButton table={table} />
                      <MRT_ToggleFullScreenButton table={table} />
                      <VerticalSeparator />
                    </Flex>
                    <PermissionWrapper
                      requiredPermissions={[Permission.WriteSimulations]}
                    >
                      <Button
                        onClick={onNewSimulationClick}
                        ref={newSimulationTriggerRef}
                        aria-label="Add new"
                        variant="primary"
                        size="small"
                        iconName="control-plus"
                      >
                        Add new
                      </Button>
                    </PermissionWrapper>
                  </Flex>
                );
              }}
              muiTableContainerProps={({ table }) => ({
                sx: {
                  flex: table.getState().isFullScreen ? undefined : '1 1 0%',
                  height: table.getState().isFullScreen
                    ? 'calc(100vh - 100px)'
                    : undefined,
                  overflow: 'auto',
                  minHeight: 0,
                  zIndex: 0,
                },
              })}
              muiTableHeadCellProps={{
                sx: {
                  '& .Mui-TableHeadCell-Content-Labels > span:has(svg[data-testid="FilterAltIcon"])':
                    {
                      display: 'none',
                    },
                },
              }}
              renderBottomToolbar={({ table }) => (
                <Flex my={1} mx={2}>
                  <Text fontSize={'11px'}>
                    {table.getFilteredRowModel().rows.length.toLocaleString()}{' '}
                    rows
                  </Text>
                </Flex>
              )}
              muiTableBodyRowProps={({ table }) => ({
                sx: {
                  backgroundColor: themeColors.surface.background.base.muted,
                  height:
                    table.getState().density === 'compact'
                      ? '30px'
                      : table.getState().density === 'comfortable'
                        ? '50px'
                        : '70px',
                },
              })}
              muiFilterTextFieldProps={{
                placeholder: 'Filter',
              }}
              muiSearchTextFieldProps={{
                placeholder: 'Search',
                variant: 'outlined',
                size: 'small',
              }}
              layoutMode="grid"
              muiTableBodyCellProps={({ cell, column }) => {
                const isClickableForSelection =
                  !isLocalPriceManager && column.id !== 'mrt-row-select';
                return {
                  onClick: () =>
                    isClickableForSelection && cell.row.toggleSelected(),
                  sx: {
                    cursor: isClickableForSelection ? 'pointer' : 'default',
                  },
                };
              }}
              globalFilterFn="contains"
              renderEmptyRowsFallback={({ table }) => (
                <FilteredEmptyList table={table} />
              )}
            />
          </LocalizationProvider>
        </ThemeProvider>
        {rowSelection.items.length > 0 && (
          <SimulationListBulkActionsBar
            rowSelection={rowSelection}
            setRowSelection={setRowSelection}
          />
        )}
      </div>
    </div>
  );
};
export default SimulationList;
